import { useState, useRef, useEffect } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { salesRecords } from "@/mocks/sales";
import { allUsers } from "@/mocks/users";

const payMethods = ["全部", "微信支付", "支付宝", "银联", "现金", "银行代扣"];

const todayStats = [
  { label: "今日收款", value: "¥28,560", sub: "12笔", color: "text-[#E8531A]", bg: "from-orange-50 to-amber-50", icon: "ri-money-cny-circle-line", trend: "+12.5%" },
  { label: "本月收款", value: "¥856,234", sub: "1,245笔", color: "text-green-600", bg: "from-green-50 to-teal-50", icon: "ri-bar-chart-line", trend: "+8.3%" },
  { label: "IoT下发", value: "1,238笔", sub: "成功率99.4%", color: "text-teal-600", bg: "from-teal-50 to-cyan-50", icon: "ri-wifi-line", trend: "+0.2%" },
  { label: "待处理", value: "7笔", sub: "需人工确认", color: "text-amber-600", bg: "from-amber-50 to-yellow-50", icon: "ri-time-line", trend: "-3笔" },
];

export default function PaymentPage() {
  const [search, setSearch] = useState("");
  const [filterMethod, setFilterMethod] = useState("全部");
  const [newModal, setNewModal] = useState(false);
  const [step, setStep] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<typeof allUsers[0] | null>(null);
  const [amount, setAmount] = useState("");
  const [payMethod, setPayMethod] = useState("微信支付");
  const [successToast, setSuccessToast] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const filtered = salesRecords.filter(r => {
    const matchMethod = filterMethod === "全部" || r.payMethod === filterMethod;
    const matchSearch = !search || r.userName.includes(search) || r.id.includes(search);
    return matchMethod && matchSearch;
  });

  const matchedUsers = userSearch.length > 1
    ? allUsers.filter(u => u.name.includes(userSearch) || u.phone.includes(userSearch))
    : [];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const hours = Array.from({ length: 12 }, (_, i) => ({ h: i * 2, v: Math.floor(Math.random() * 8000 + 2000) }));
    const maxV = Math.max(...hours.map(d => d.v));
    const barW = (w - 40) / hours.length;

    hours.forEach((d, i) => {
      const barH = ((d.v / maxV) * (h - 40));
      const x = 20 + i * barW + barW * 0.1;
      const bw = barW * 0.8;
      const y = h - 20 - barH;
      const isCurrent = d.h === 8;

      const grad = ctx.createLinearGradient(x, y, x, h - 20);
      grad.addColorStop(0, isCurrent ? "rgba(232,83,26,1)" : "rgba(232,83,26,0.5)");
      grad.addColorStop(1, isCurrent ? "rgba(232,83,26,0.4)" : "rgba(232,83,26,0.1)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x, y, bw, barH, 3);
      ctx.fill();

      if (isCurrent) {
        ctx.fillStyle = "#E8531A";
        ctx.font = "bold 9px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(`¥${(d.v / 1000).toFixed(1)}k`, x + bw / 2, y - 4);
      }

      ctx.fillStyle = "rgba(100,100,100,0.6)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`${d.h}:00`, x + bw / 2, h - 5);
    });
  }, []);

  const handleConfirm = () => {
    if (step < 3) { setStep(s => s + 1); return; }
    setNewModal(false);
    setStep(1);
    setSelectedUser(null);
    setAmount("");
    setUserSearch("");
    setSuccessToast(true);
    setTimeout(() => setSuccessToast(false), 3000);
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">营业收费窗口</h1>
            <p className="text-sm text-gray-500 mt-0.5">收费记录 · IoT指令下发 · 实时营收监控</p>
          </div>
          <button
            onClick={() => { setNewModal(true); setStep(1); }}
            className="flex items-center gap-2 bg-[#E8531A] text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-[#d44a15] cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-circle-line text-base"></i>
            新建收费
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {todayStats.map((s, i) => (
            <div key={i} className={`bg-gradient-to-br ${s.bg} rounded-xl p-4 border border-white`}>
              <div className="flex items-center justify-between mb-2">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white">
                  <i className={`${s.icon} ${s.color} text-lg`}></i>
                </div>
                <span className={`text-xs font-medium ${s.trend.startsWith("+") ? "text-green-600" : "text-red-500"}`}>{s.trend}</span>
              </div>
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{s.label} · {s.sub}</div>
            </div>
          ))}
        </div>

        {/* Today Revenue Chart */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <i className="ri-line-chart-line text-[#E8531A]"></i>
              <span className="text-sm font-semibold text-gray-800">今日分时营收（元）</span>
            </div>
            <span className="text-xs text-gray-400">实时更新</span>
          </div>
          <canvas ref={canvasRef} width={900} height={100} className="w-full" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="搜索订单号、用户名..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {payMethods.map(m => (
              <button
                key={m}
                onClick={() => setFilterMethod(m)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterMethod === m ? "bg-[#E8531A] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
              >
                {m}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            <i className="ri-download-line"></i>导出
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {["订单号", "用户姓名", "金额", "购气量", "支付方式", "支付时间", "IoT状态", "操作"].map(h => (
                  <th key={h} className="text-left text-xs font-medium text-gray-500 px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-xs font-mono text-gray-500">{r.id}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">{r.userName}</td>
                  <td className="px-4 py-3 text-sm font-bold text-[#E8531A]">¥{r.amount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{r.gasVolume.toFixed(2)} m³</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{r.payMethod}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{r.payTime}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.iotStatus === "已下发" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
                      {r.iotStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap">打印小票</button>
                      <button className="text-xs text-gray-400 hover:underline cursor-pointer whitespace-nowrap">发票</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Payment Modal */}
      {newModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setNewModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[520px] max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-900">新建收费</h3>
              <button onClick={() => setNewModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            {/* Steps */}
            <div className="flex items-center gap-0 mb-6">
              {["选择用户", "输入金额", "确认支付"].map((s, i) => (
                <div key={i} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step > i + 1 ? "bg-green-500 text-white" : step === i + 1 ? "bg-[#E8531A] text-white" : "bg-gray-100 text-gray-400"}`}>
                      {step > i + 1 ? <i className="ri-check-line text-xs"></i> : i + 1}
                    </div>
                    <span className={`text-[10px] mt-1 whitespace-nowrap ${step === i + 1 ? "text-[#E8531A]" : "text-gray-400"}`}>{s}</span>
                  </div>
                  {i < 2 && <div className={`flex-1 h-0.5 mx-1 mb-4 ${step > i + 1 ? "bg-green-400" : "bg-gray-100"}`} />}
                </div>
              ))}
            </div>

            {step === 1 && (
              <div>
                <div className="relative mb-3">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="输入用户名、手机号..."
                    className="w-full pl-8 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]"
                  />
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {(matchedUsers.length > 0 ? matchedUsers : allUsers.slice(0, 5)).map(u => (
                    <button
                      key={u.id}
                      onClick={() => setSelectedUser(u)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer flex items-center justify-between ${selectedUser?.id === u.id ? "bg-orange-50 border border-[#E8531A]/30" : "bg-gray-50 hover:bg-gray-100"}`}
                    >
                      <div>
                        <span className={`font-medium ${selectedUser?.id === u.id ? "text-[#E8531A]" : "text-gray-700"}`}>{u.name}</span>
                        <span className="text-xs text-gray-400 ml-2">{u.phone} · {u.id}</span>
                      </div>
                      <span className={`text-xs font-bold ${u.balance < 100 ? "text-red-600" : "text-gray-600"}`}>¥{u.balance.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="bg-orange-50 rounded-xl p-3 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#E8531A] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{selectedUser?.name[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">{selectedUser?.name}</p>
                    <p className="text-xs text-gray-500">当前余额：¥{selectedUser?.balance.toFixed(2)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[100, 200, 300, 500, 1000, 2000].map(a => (
                    <button
                      key={a}
                      onClick={() => setAmount(String(a))}
                      className={`py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${amount === String(a) ? "bg-[#E8531A] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                    >
                      ¥{a}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">¥</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="自定义金额"
                    className="w-full pl-7 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]"
                  />
                </div>
                {amount && (
                  <div className="mt-3 bg-orange-50 rounded-lg p-3 text-sm text-gray-700">
                    购气量约：<span className="font-bold text-[#E8531A]">{(Number(amount) / 3.5).toFixed(2)} m³</span>
                    <span className="text-xs text-gray-400 ml-2">（¥3.50/m³）</span>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div>
                <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-2.5">
                  {[
                    { k: "用户姓名", v: selectedUser?.name },
                    { k: "购气金额", v: `¥${amount}`, bold: true },
                    { k: "购气量", v: `${(Number(amount) / 3.5).toFixed(2)} m³` },
                    { k: "充值后余额", v: `¥${((selectedUser?.balance || 0) + Number(amount)).toFixed(2)}` },
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-500">{row.k}</span>
                      <span className={`${row.bold ? "font-bold text-[#E8531A] text-base" : "font-medium text-gray-800"}`}>{row.v}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-2 block">支付方式</label>
                  <div className="grid grid-cols-3 gap-2">
                    {["微信支付", "支付宝", "现金", "银联", "银行代扣", "其他"].map(m => (
                      <button
                        key={m}
                        onClick={() => setPayMethod(m)}
                        className={`py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${payMethod === m ? "bg-[#E8531A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-5">
              {step > 1 && (
                <button onClick={() => setStep(s => s - 1)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">上一步</button>
              )}
              <button onClick={() => setNewModal(false)} className="px-4 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button
                onClick={handleConfirm}
                disabled={(step === 1 && !selectedUser) || (step === 2 && !amount)}
                className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap disabled:opacity-50"
              >
                {step === 3 ? "确认收款" : "下一步"}
              </button>
            </div>
          </div>
        </div>
      )}

      {successToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-xl flex items-center gap-2 z-50">
          <i className="ri-checkbox-circle-line text-lg"></i>
          <span className="text-sm font-medium">收费成功！IoT指令已下发</span>
        </div>
      )}
    </MainLayout>
  );
}
